import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { useTheme } from "@emotion/react";
import MaestroNavbar from "./blocks/MaestroNavbar.js";
import MaestroFooter from "./blocks/MaestroFooter.js";
import Seo from "./blocks/Seo.js";

/**
 * Privacy Policy.
 *
 * Rewritten from the alfie/stanza-react-app calendar-oriented template for
 * Maestro's K-12 STEM education context. Key differences:
 *   - Data types reflect classroom use (assessment data, rosters, teacher
 *     work product) instead of calendar events.
 *   - FERPA + COPPA are explicitly addressed at the top since Maestro
 *     interacts with student data indirectly through teacher-uploaded
 *     content.
 *   - Ad tech / publisher-tracking sections were removed; Maestro does
 *     not run ads or share user data with advertising exchanges.
 *
 * Placeholders that need legal review before launch:
 *   - Entity name (currently "Lemonade Labs LLC") + any 501(c)(3) status
 *   - Contact / data-request email addresses
 *   - Data retention windows for teacher work product
 */
const Privacy = () => {
  const theme = useTheme();

  const Heading = ({ children }) => (
    <Typography
      component="h2"
      sx={{
        fontSize: "1.375rem",
        fontWeight: 700,
        letterSpacing: "-0.01em",
        mt: 5,
        mb: 2,
      }}
    >
      {children}
    </Typography>
  );

  const P = ({ children }) => (
    <Typography
      component="p"
      sx={{
        fontSize: "1rem",
        color: theme.palette.paper.primary,
        lineHeight: 1.65,
        mb: 2,
      }}
    >
      {children}
    </Typography>
  );

  const UL = ({ children }) => (
    <Box
      component="ul"
      sx={{
        m: 0,
        mb: 2,
        pl: 3,
        // Force <li> to inherit the theme's font family — without this,
        // raw <li> elements fall back to the browser default and look
        // like a different typeface than the paragraphs around them.
        fontFamily: theme.typography.fontFamily,
        "& li": {
          fontFamily: "inherit",
          fontSize: "1rem",
          color: theme.palette.paper.primary,
          lineHeight: 1.65,
          mb: 1,
        },
      }}
    >
      {children}
    </Box>
  );

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "#000000",
        color: theme.palette.paper.primary,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Seo
        title="Privacy Policy · Maestro"
        description="Privacy Policy for Maestro, AI + coaching for K-12 STEM teachers. How we collect, use, and protect teacher and student data."
      />

      <MaestroNavbar variant="dark" />

      <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 }, flexGrow: 1 }}>
        <Typography
          component="h1"
          sx={{
            fontSize: { xs: "2rem", md: "2.75rem" },
            fontWeight: 800,
            letterSpacing: "-0.03em",
            lineHeight: 1.1,
            mb: 1,
            color: theme.palette.paper.primary,
          }}
        >
          Privacy Policy
        </Typography>
        <Typography
          sx={{
            fontSize: "0.9375rem",
            color: "rgba(255,255,255,0.6)",
            mb: 5,
          }}
        >
          Last updated: July 2, 2026
        </Typography>

        <P>
          This Privacy Policy explains how Lemonade Labs LLC
          ("Maestro," "we," "us," or "our") collects, uses, and protects
          information in connection with the Maestro platform and related
          services (the "Platform"). The Platform provides AI-generated
          teaching materials and coaching support for K-12 STEM teachers.
        </P>
        <P>
          We built Maestro around three commitments: teachers own their
          workflow, student data stays with the school, and AI is used to
          support teacher judgment, never to replace it.
        </P>

        <Heading>1. Who this policy applies to</Heading>
        <P>This policy applies to:</P>
        <UL>
          <li>
            <strong>Teachers, coaches, and school administrators</strong>{" "}
            who create an account or interact with the Platform.
          </li>
          <li>
            <strong>School and district partners</strong> that enter into a
            partnership agreement with Maestro to bring the Platform to
            their teachers.
          </li>
          <li>
            <strong>Visitors to our website</strong> at withmaestro.org and
            teachwithmaestro.org.
          </li>
        </UL>
        <P>
          Students do not create accounts on the Platform. Any student data
          reaches Maestro only through teacher-uploaded materials (such as
          exit tickets or class rosters) and is subject to the additional
          protections described below.
        </P>

        <Heading>2. Information we collect</Heading>
        <Typography
          component="h3"
          sx={{ fontSize: "1.0625rem", fontWeight: 700, mt: 3, mb: 1.5 }}
        >
          From teachers and school users
        </Typography>
        <UL>
          <li>
            <strong>Account information:</strong> name, work email, school
            or district affiliation, grade band, subject taught.
          </li>
          <li>
            <strong>User content:</strong> lesson plans, exit tickets,
            assessment results, curricular scope-and-sequence, and other
            materials you upload or share with the Platform.
          </li>
          <li>
            <strong>Coaching call notes:</strong> summaries of weekly
            coaching conversations used to iterate your personalized AI
            skill.
          </li>
          <li>
            <strong>Usage data:</strong> which Platform features you use,
            which materials you generate, and how you interact with the
            outputs.
          </li>
        </UL>

        <Typography
          component="h3"
          sx={{ fontSize: "1.0625rem", fontWeight: 700, mt: 3, mb: 1.5 }}
        >
          From website visitors
        </Typography>
        <UL>
          <li>
            <strong>Standard log data:</strong> IP address, browser type,
            device type, referring URL, and pages visited.
          </li>
          <li>
            <strong>Waitlist and contact form submissions:</strong> the
            information you voluntarily provide when signing up for our
            waitlist or contacting us.
          </li>
        </UL>

        <Typography
          component="h3"
          sx={{ fontSize: "1.0625rem", fontWeight: 700, mt: 3, mb: 1.5 }}
        >
          About students (indirect)
        </Typography>
        <P>
          When a teacher uploads classroom data, the file may contain
          student identifiers (such as names, student IDs, or assessment
          scores). We treat any such data as confidential, use it only to
          generate the outputs the teacher requested, and never sell it or
          share it with advertising networks.
        </P>

        <Heading>3. How we use information</Heading>
        <UL>
          <li>
            To operate the Platform and deliver personalized materials to
            teachers.
          </li>
          <li>
            To iterate each teacher's AI skill based on classroom outcomes
            and weekly coaching notes.
          </li>
          <li>
            To communicate with you about your account, changes to the
            Platform, and educational research findings.
          </li>
          <li>
            To measure aggregate outcomes across the Maestro network for
            program reporting (typically de-identified and aggregated).
          </li>
          <li>
            To respond to legal requests or protect the rights, safety, and
            property of Maestro, our users, and the public.
          </li>
        </UL>
        <P>
          We do not use identifiable student data to train third-party AI
          models. When Platform features require sending content to a
          third-party model (for example, to generate a scaffolded
          worksheet), we minimize the data included and prefer teacher-facing
          content over student-facing content.
        </P>

        <Heading>4. FERPA, COPPA, and student data</Heading>
        <P>
          Maestro treats student information provided by educators as
          confidential and subject to any applicable federal and state
          student privacy laws, including the Family Educational Rights and
          Privacy Act (FERPA) and, where relevant, the Children's Online
          Privacy Protection Act (COPPA).
        </P>
        <P>
          When we act as a "school official" with a "legitimate educational
          interest" under FERPA (34 CFR § 99.31(a)(1)(i)(B)), we perform
          services on behalf of and under the control of your school or
          district and only use student data for the purposes authorized by
          that school or district. Schools and districts remain the
          responsible party for student data under FERPA.
        </P>
        <P>
          Students under the age of 13 do not have their own accounts on
          the Platform. To the extent COPPA applies, we rely on the school
          or district providing consent on behalf of parents, in accordance
          with FTC guidance on the "school authorization" method.
        </P>

        <Heading>5. How we share information</Heading>
        <P>
          We share information only in the ways described below. We do not
          sell personal information, and we do not share teacher or student
          data with advertisers.
        </P>
        <UL>
          <li>
            <strong>Service providers</strong> that help us operate the
            Platform (cloud hosting, AI model providers, error monitoring,
            email delivery), under contracts that require them to protect
            your data and use it only for authorized purposes.
          </li>
          <li>
            <strong>Your school, district, or coach</strong> for the
            purpose of delivering the Platform's coaching and reporting
            services to you.
          </li>
          <li>
            <strong>With your consent</strong> or at your direction (for
            example, if you choose to share a Maestro-generated resource
            publicly).
          </li>
          <li>
            <strong>To comply with law</strong> or respond to lawful
            requests from government authorities.
          </li>
        </UL>

        <Heading>6. Data security</Heading>
        <P>
          We use industry-standard safeguards, including encryption in
          transit and at rest, role-based access controls, and audit
          logging, to protect the information you provide. No system is
          perfectly secure; if we learn of a security breach that affects
          your data, we will notify you and applicable authorities as
          required by law.
        </P>

        <Heading>7. Data retention</Heading>
        <P>
          We retain teacher work product for the duration of your active use
          of the Platform, plus a limited window after account closure to
          support recovery and reporting requirements. Aggregated,
          de-identified data may be retained longer for research and
          program-improvement purposes. Schools and districts may request
          deletion of their teachers' data at any time.
        </P>

        <Heading>8. Your rights and choices</Heading>
        <P>Depending on where you live, you may have the right to:</P>
        <UL>
          <li>Access the information we hold about you.</li>
          <li>Correct information that is inaccurate or incomplete.</li>
          <li>Request deletion of your account and associated data.</li>
          <li>
            Object to or restrict certain processing, including the use of
            your data to improve the Platform.
          </li>
          <li>Receive a portable copy of the information you provided.</li>
        </UL>
        <P>
          To exercise any of these rights, email us at{" "}
          <Box
            component="a"
            href="mailto:founders@teachwithmaestro.org"
            sx={{ color: theme.palette.success.main, fontWeight: 600 }}
          >
            founders@teachwithmaestro.org
          </Box>
          . We will respond within the time frame required by applicable
          law.
        </P>

        <Heading>9. Cookies and analytics</Heading>
        <P>
          Our website uses a small number of cookies to keep you signed in,
          remember your preferences, and understand how the site is used
          (for example, which pages are most visited). We do not use
          advertising or cross-site tracking cookies. You can control cookies
          through your browser settings.
        </P>

        <Heading>10. Children's information</Heading>
        <P>
          The Platform is designed for use by adults working with students.
          Students do not have their own accounts and do not directly
          interact with the Platform. Any student information reaches
          Maestro only through educator-provided content, and is treated
          under the FERPA / COPPA framework described above.
        </P>

        <Heading>11. Changes to this policy</Heading>
        <P>
          We may update this Privacy Policy from time to time. When we make
          meaningful changes, we will notify you by email and by posting the
          updated version on our website with a new "Last updated" date.
        </P>

        <Heading>12. Contact us</Heading>
        <P>
          If you have questions about this Privacy Policy or how we handle
          data, please email{" "}
          <Box
            component="a"
            href="mailto:founders@teachwithmaestro.org"
            sx={{ color: theme.palette.success.main, fontWeight: 600 }}
          >
            founders@teachwithmaestro.org
          </Box>
          .
        </P>
      </Container>

      <MaestroFooter variant="dark" />
    </Box>
  );
};

export default Privacy;
